import type { JSX } from 'react';

import { H2 } from '#/components/Text/H2';
import { H3 } from '#/components/Text/H3';
import { Underline } from '#/components/Text/Underline';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { MainChartData } from '#/modules/salary/types/MainChartData';

import { ChartTextContainer } from './MainChartHeader.styled';

type Props = {
  data: MainChartData;
  initialized: boolean;
  loading: boolean;
};

export function MainChartHeader({ data, initialized, loading }: Props) {
  const { i18n } = useI18n();

  let headingText: JSX.Element | string = 'Loading... please wait';
  let subHeadingText: JSX.Element | string = '';

  if (!loading && data === null && initialized) {
    headingText = i18n('We are still a work {{s}}in progress{{/s}}', {
      s: <Underline />,
    });
    subHeadingText = i18n(
      "We don't have enough data about this job title, please try another combination of information",
    );
  }

  if (!loading && data && initialized) {
    const { filtersUsed = {}, submittedData = {} } = data;
    const locationFilterApplied = filtersUsed && submittedData.city;

    headingText = locationFilterApplied
      ? i18n('{{s}}%(jobTitle)s{{/s}} Salaries in %(workLocation)s', {
          jobTitle: submittedData.jobTitleSelected ?? '',
          workLocation: `${submittedData.city}, ${submittedData.stateCode}`,
          s: <Underline />,
        })
      : i18n('{{s}}%(jobTitle)s{{/s}} Salaries', {
          jobTitle: submittedData.jobTitleSelected ?? '',
          s: <Underline />,
        });
    subHeadingText = '';
  }

  if (!loading && !initialized) {
    headingText = i18n('You Should Be {{s}}Paid Fairly{{/s}}', {
      s: <Underline />,
    });
    subHeadingText = i18n(
      'Learn what people at nonprofits earn, in different jobs, different cities, and more!',
    );
  }

  return (
    <ChartTextContainer>
      <H2 mb={0}>{headingText}</H2>
      {subHeadingText ? <H3 mb={0}>{subHeadingText}</H3> : null}
    </ChartTextContainer>
  );
}
